import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Loader2, Send, AlertTriangle, Clock, BadgeCheck, PowerOff,
} from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

/**
 * Step 8 — Status Listing + Listing Readiness + Submit/Resubmit for Owner.
 * Props: property (p), onChanged() to reload the property after status change.
 */
export default function ListingPanel({ property, onChanged }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readinessError, setReadinessError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setReadinessError(false);
    apiClient
      .get(`/properties/${property.id}/readiness`)
      .then((r) => setReadiness(r.data))
      .catch(() => { setReadiness(null); setReadinessError(true); })
      .finally(() => setLoading(false));
  }, [property.id]);

  useEffect(() => { load(); }, [load, property.status]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiClient.post(`/properties/${property.id}/submit`);
      toast.success("Property berhasil diajukan untuk review");
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const status = property.status;

  const Banner = () => {
    if (status === "pending")
      return (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="listing-banner-pending">
          <Clock className="text-amber-600 shrink-0" size={20} />
          <div>
            <p className="font-semibold text-amber-800">Menunggu Review</p>
            <p className="text-sm text-amber-700">Property sedang ditinjau oleh Admin. Anda akan diberi tahu setelah ada keputusan.</p>
          </div>
        </div>
      );
    if (status === "published")
      return (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="listing-banner-published">
          <BadgeCheck className="text-emerald-600 shrink-0" size={20} />
          <div>
            <p className="font-semibold text-emerald-800">Property Aktif</p>
            <p className="text-sm text-emerald-700">Property Anda sudah tayang dan dapat dilihat oleh calon penyewa.</p>
          </div>
        </div>
      );
    if (status === "inactive")
      return (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4" data-testid="listing-banner-inactive">
          <PowerOff className="text-muted-foreground shrink-0" size={20} />
          <div>
            <p className="font-semibold text-foreground">Property Tidak Aktif</p>
            <p className="text-sm text-muted-foreground">Property dinonaktifkan. Edit dan aktifkan kembali kapan saja.</p>
          </div>
        </div>
      );
    if (status === "rejected")
      return (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="listing-banner-rejected">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 shrink-0" size={20} />
            <div>
              <p className="font-semibold text-red-800">Listing Ditolak</p>
              <p className="text-sm text-red-700" data-testid="rejection-reason">
                {property.rejection_reason || "Admin menolak listing ini. Silakan perbaiki lalu ajukan kembali."}
              </p>
            </div>
          </div>
        </div>
      );
    // draft
    return (
      <div className="flex items-start gap-3 rounded-xl border border-border bg-secondary/40 p-4" data-testid="listing-banner-draft">
        <Send className="text-primary shrink-0" size={20} />
        <div>
          <p className="font-semibold text-foreground">Lengkapi Listing</p>
          <p className="text-sm text-muted-foreground">Pastikan semua bagian wajib lengkap, lalu ajukan untuk review Admin.</p>
        </div>
      </div>
    );
  };

  const canSubmit = readiness?.can_submit && (status === "draft" || status === "rejected");
  const submitLabel = status === "rejected" ? "Ajukan Kembali" : "Ajukan untuk Review";

  return (
    <div data-testid="listing-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-lg font-semibold">Status Listing</h3>
      </div>

      <Banner />

      {/* Listing Readiness checklist */}
      <div className="mt-5">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">Listing Readiness</p>
        {loading ? (
          <div className="py-4 grid place-items-center"><Loader2 className="animate-spin text-primary" size={20} /></div>
        ) : readinessError ? (
          <div className="flex items-center justify-between gap-3 text-sm text-destructive" data-testid="readiness-error">
            <span>Gagal memuat status kelengkapan.</span>
            <button onClick={load} className="font-semibold underline hover:text-primary" data-testid="readiness-retry">Coba lagi</button>
          </div>
        ) : !readiness ? (
          <p className="text-sm text-muted-foreground">Gagal memuat status kelengkapan.</p>
        ) : (
          <ul className="space-y-1.5" data-testid="readiness-list">
            {readiness.items.map((it) => (
              <li key={it.key} className="flex items-center gap-2 text-sm" data-testid={`readiness-${it.key}`} data-ok={it.ok}>
                {it.ok ? (
                  <CheckCircle2 size={17} className="text-emerald-600 shrink-0" />
                ) : (
                  <Circle size={17} className="text-muted-foreground shrink-0" />
                )}
                <span className={it.ok ? "text-foreground" : "text-muted-foreground"}>
                  {it.label}{!it.required && <span className="ml-1 text-xs text-muted-foreground">(opsional)</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Submit / Resubmit */}
      {(status === "draft" || status === "rejected") && (
        <div className="mt-5">
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit || submitting}
            data-testid="submit-review-button"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {submitting ? "Mengajukan..." : submitLabel}
          </button>
          {readiness && !readiness.can_submit && (
            <p className="mt-2 text-xs text-destructive" data-testid="submit-missing-note">
              Belum bisa diajukan. Lengkapi: {readiness.missing.join(", ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
